// ---------------------------------------------------------------------------
// Foundation grid orientation assignment
//
// Adapted from computeRoomGridOrientations / groupByOrientation in
// packages/shared/src/routing/autoPipeRouting/buildNodeGrid.logic.ts, which
// bins room-polygon wall segments by direction to find each room's dominant
// grid axis. Here the same binning groups apparatus foundations by their
// dominant alignment instead.
// ---------------------------------------------------------------------------
const NUM_ORIENTATION_BINS = 16; // π/32 rad resolution → 16 bins covering [0, π/2)
const ORIENTATION_BIN_ANGLE = Math.PI / 32; // width of each bin
/**
 * Rotates (dx, dy) by successive 90° CCW steps (x,y → -y,x) until both
 * components are ≥ 0, mapping any direction into the first quadrant [0, π/2].
 */
function normalizeToQ1(dx, dy) {
    let x = dx, y = dy;
    for (let i = 0; i < 4; i++) {
        if (x >= 0 && y >= 0)
            return { x, y };
        const nx = -y, ny = x;
        x = nx;
        y = ny;
    }
    return { x, y };
}
/** Maps a direction vector to a bin index in [0, NUM_ORIENTATION_BINS-1]. */
function directionToBinIdx(dx, dy) {
    const { x, y } = normalizeToQ1(dx, dy);
    return Math.min(NUM_ORIENTATION_BINS - 1, Math.floor(Math.atan2(y, x) / ORIENTATION_BIN_ANGLE));
}
function emptyOrientationBins() {
    return Array.from({ length: NUM_ORIENTATION_BINS }, () => ({
        sumX: 0, sumY: 0, totalLength: 0, foundationLengths: new Map(),
    }));
}
/** Accumulates all edge segments of `activeIds` foundations into `bins`. */
function accumulateIntoBins(foundations, activeIds, bins) {
    for (const { points, id } of foundations) {
        if (!activeIds.has(id))
            continue;
        const n = points.length;
        for (let i = 0; i < n; i++) {
            const [ax, ay] = points[i];
            const [bx, by] = points[(i + 1) % n];
            const dx = bx - ax, dy = by - ay;
            const len = Math.sqrt(dx * dx + dy * dy);
            if (len < 1e-9)
                continue;
            // Rotate to Q1; length is preserved by rotation.
            const { x, y } = normalizeToQ1(dx, dy);
            const binIdx = directionToBinIdx(dx, dy);
            const bin = bins[binIdx];
            bin.sumX += x;
            bin.sumY += y;
            bin.totalLength += len;
            bin.foundationLengths.set(id, (bin.foundationLengths.get(id) ?? 0) + len);
        }
    }
}
/**
 * Returns the index of the first bin in the adjacent pair (i, i+1 mod N) whose
 * combined total segment length is greatest.  Bins 15 and 0 are also considered
 * adjacent (wrap-around).
 */
function findBestAdjacentPair(bins) {
    let bestIdx = 0, bestLen = -1;
    for (let i = 0; i < NUM_ORIENTATION_BINS; i++) {
        const total = bins[i].totalLength + bins[(i + 1) % NUM_ORIENTATION_BINS].totalLength;
        if (total > bestLen) {
            bestLen = total;
            bestIdx = i;
        }
    }
    return bestIdx;
}
/**
 * Computes the dominant alignment direction (unit vector in Q1) from the pair
 * (binA, binA+1 mod N).
 *
 * For the wrap-around pair (15, 0): bin 0's accumulated vector is rotated 90°
 * CCW before summing with bin 15's vector so that both contributions point in
 * the same angular neighbourhood.  The combined sum is then normalised to Q1.
 */
function directionFromPair(bins, binA) {
    const binB = (binA + 1) % NUM_ORIENTATION_BINS;
    const isWrap = binA === NUM_ORIENTATION_BINS - 1; // pair (15, 0)
    let totalX = bins[binA].sumX;
    let totalY = bins[binA].sumY;
    if (isWrap) {
        // Rotate bin 0 by 90° CCW to align with bin 15: (x,y) → (-y, x)
        totalX += -bins[binB].sumY;
        totalY += bins[binB].sumX;
    }
    else {
        totalX += bins[binB].sumX;
        totalY += bins[binB].sumY;
    }
    const { x, y } = normalizeToQ1(totalX, totalY);
    const len = Math.sqrt(x * x + y * y);
    if (len < 1e-9)
        return { dx: 1, dy: 0 }; // fallback: axis-aligned
    return { dx: x / len, dy: y / len };
}
/**
 * Iteratively assigns a dominant alignment direction to every apparatus
 * foundation.
 *
 * Each iteration:
 *  1. Bin all remaining foundations' edge segments by direction (Q1-normalised).
 *  2. Find the two adjacent bins with the greatest combined edge length.
 *  3. Compute the dominant direction for those bins.
 *  4. Assign that direction to foundations whose dominant bin (highest
 *     per-foundation length) falls in the selected pair.
 *  5. Remove assigned foundations; repeat until none remain.
 *
 * Returns a map from foundation id → unit direction vector (dx, dy) in Q1.
 */
export function computeFoundationAlignments(foundations) {
    const result = new Map();
    const remaining = new Set(foundations.map((f) => f.id));
    while (remaining.size > 0) {
        const bins = emptyOrientationBins();
        accumulateIntoBins(foundations, remaining, bins);
        const bestA = findBestAdjacentPair(bins);
        const bestB = (bestA + 1) % NUM_ORIENTATION_BINS;
        const direction = directionFromPair(bins, bestA);
        // Assign foundations whose dominant bin falls in {bestA, bestB}.
        const toAssign = new Set();
        for (const id of remaining) {
            let maxLen = 0, domBin = 0;
            for (let i = 0; i < NUM_ORIENTATION_BINS; i++) {
                const len = bins[i].foundationLengths.get(id) ?? 0;
                if (len > maxLen) {
                    maxLen = len;
                    domBin = i;
                }
            }
            if (domBin === bestA || domBin === bestB)
                toAssign.add(id);
        }
        // Fallback: if no foundation's dominant bin matched, take any foundation
        // with a contribution to the pair — prevents infinite loops on degenerate input.
        if (toAssign.size === 0) {
            for (const id of remaining) {
                const inPair = (bins[bestA].foundationLengths.get(id) ?? 0)
                    + (bins[bestB].foundationLengths.get(id) ?? 0);
                if (inPair > 0)
                    toAssign.add(id);
            }
        }
        if (toAssign.size === 0) {
            // Last resort: assign everything remaining.
            for (const id of remaining)
                toAssign.add(id);
        }
        for (const id of toAssign) {
            result.set(id, direction);
            remaining.delete(id);
        }
    }
    return result;
}
/**
 * Groups apparatus foundations by identical dominant alignment direction,
 * as computed by computeFoundationAlignments.
 */
export function groupFoundationsByAlignment(foundations) {
    const alignments = computeFoundationAlignments(foundations);
    const groups = new Map();
    for (const foundation of foundations) {
        const orientation = alignments.get(foundation.id) ?? { dx: 1, dy: 0 };
        // Fixed-precision string key groups bit-identical directions together.
        const key = `${orientation.dx.toFixed(9)}_${orientation.dy.toFixed(9)}`;
        if (!groups.has(key))
            groups.set(key, { orientation, foundations: [] });
        groups.get(key).foundations.push(foundation);
    }
    const groupedCount = [...groups.values()].reduce((sum, group) => sum + group.foundations.length, 0);
    if (groupedCount !== foundations.length) {
        throw new Error(`groupFoundationsByAlignment: expected all ${foundations.length} foundations to be assigned to an alignment group, but only ${groupedCount} were.`);
    }
    return [...groups.values()];
}
